import { doc, getDoc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import cacheManager from './cache_manager';
import Cookies from 'js-cookie';
import { auth } from './firebase';
import notificationManager from './sessions/notification_session';
import subscriptionManager from './sessions/subscription_session';

class SessionManager {
  constructor() {
    this._session = null;
    this._timeouts = {
      inactivity: null,
      refresh: null
    };
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    this.REFRESH_INTERVAL = 55 * 60 * 1000;  // 55 minutes
    this._initializationLock = null;
    this._validateSession = httpsCallable(functions, 'validateSession');
    
    // # Initialize BroadcastChannel for cross-tab communication
    if (typeof BroadcastChannel !== 'undefined') {
      this._sessionChannel = new BroadcastChannel('session_channel');
      this._setupChannelListeners();
    }

    // # Add firestore listener functionality
    this._listeners = new Map();
  }

  // # Setup BroadcastChannel listeners
  _setupChannelListeners() {
    this._sessionChannel.onmessage = async (event) => {
      switch (event.data.type) {
        case 'session_update':
          this._session = event.data.session;
          // # Update subscription manager with new session
          subscriptionManager.setSession(this._session);
          break;
        case 'session_clear':
          this._session = null;
          this._clearTimeouts();
          break;
        default:
          break;
      }
    };
  }

  // # Broadcast session update to other tabs
  _broadcastSessionUpdate() {
    if (this._sessionChannel) {
      this._sessionChannel.postMessage({
        type: 'session_update',
        session: this._session
      });
    }
    
    // # Update subscription manager with latest session data
    if (this._session) {
      subscriptionManager.setSession(this._session);
    }
  }

  // # Get device information
  async _getDeviceInfo() {
    const deviceId = this._generateDeviceId();
    
    return {
      deviceId,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      lastActive: new Date().toISOString()
    };
  }

  // # Generate a unique device ID
  _generateDeviceId() {
    // # Check for existing device ID in localStorage
    let deviceId = localStorage.getItem('device_id');
    
    if (!deviceId) {
      // # Generate a new device ID if none exists
      deviceId = 'device_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('device_id', deviceId);
    }
    
    return deviceId;
  }

  // # Setup session timeouts
  _setupTimeouts() {
    // # Clear any existing timeouts
    this._clearTimeouts();
    
    // # Set inactivity timeout
    this._timeouts.inactivity = setTimeout(() => {
      this.handleTimeout('inactivity');
    }, this.INACTIVITY_TIMEOUT);
    
    // # Set refresh timeout
    this._timeouts.refresh = setTimeout(() => {
      this.refreshSession();
    }, this.REFRESH_INTERVAL);
    
    // # Setup activity listeners
    this._setupActivityListeners();
  }

  // # Handle timeout events
  async handleTimeout(type) {
    if (type === 'inactivity') {
      console.log('Session timed out due to inactivity');
      await this.clearSession();
    }
  }

  // # Setup activity listeners to reset inactivity timeout
  _setupActivityListeners() {
    const resetInactivityTimeout = () => {
      // # Clear existing timeout
      if (this._timeouts.inactivity) {
        clearTimeout(this._timeouts.inactivity);
      }
      
      // # Set new timeout
      this._timeouts.inactivity = setTimeout(() => {
        this.handleTimeout('inactivity');
      }, this.INACTIVITY_TIMEOUT);
      
      // # Update last active timestamp if session exists
      if (this._session) {
        this._session.lastActive = new Date().toISOString();
        
        // # Update device info in session
        if (this._session.deviceInfo) {
          this._session.deviceInfo.lastActive = new Date().toISOString();
        }
        
        // # Broadcast session update
        this._broadcastSessionUpdate();
      }
    };
    
    // # Add event listeners for user activity
    window.addEventListener('mousemove', resetInactivityTimeout);
    window.addEventListener('keydown', resetInactivityTimeout);
    window.addEventListener('click', resetInactivityTimeout);
    window.addEventListener('scroll', resetInactivityTimeout);
    window.addEventListener('touchstart', resetInactivityTimeout);
  }

  // # Clear all timeouts
  _clearTimeouts() {
    // # Clear inactivity timeout
    if (this._timeouts.inactivity) {
      clearTimeout(this._timeouts.inactivity);
      this._timeouts.inactivity = null;
    }
    
    // # Clear refresh timeout
    if (this._timeouts.refresh) {
      clearTimeout(this._timeouts.refresh);
      this._timeouts.refresh = null;
    }
  }

  // # INTEGRATED FIRESTORE LISTENER METHODS

  // # Setup user document listener
  setupUserListener(uid) {
    if (this._listeners.has(`user_${uid}`)) return;

    const unsubscribe = onSnapshot(doc(db, 'users', uid), (doc) => {
      if (doc.exists()) {
        // # Update cache with new data
        cacheManager.set(`user_${uid}`, doc.data());
      }
    }, (error) => {
      console.error('Error in user listener:', error);
    });

    this._listeners.set(`user_${uid}`, unsubscribe);
  }

  // # Setup subscription document listener - delegates to subscription manager
  setupSubscriptionListener(uid) {
    subscriptionManager.setupSubscriptionListener(uid);
  }

  // # Remove specific listener
  removeListener(type, uid) {
    if (type === 'subscription') {
      subscriptionManager.removeListener(uid);
      return;
    }
    
    const key = `${type}_${uid}`;
    const unsubscribe = this._listeners.get(key);
    
    if (unsubscribe) {
      unsubscribe();
      this._listeners.delete(key);
    }
  }

  // # Remove all listeners
  removeAllListeners() {
    this._listeners.forEach((unsubscribe) => unsubscribe());
    this._listeners.clear();
    
    // # Also remove subscription listeners
    subscriptionManager.removeAllListeners();
  }

  // # Initialize all listeners for a user
  initializeListeners(uid) {
    this.setupUserListener(uid);
    this.setupSubscriptionListener(uid);
  }

  // # Initialize session
  async initializeSession(user, options = {}) {
    // # Default options
    const defaultOptions = {
      forceRefresh: false,
      validateSession: true
    };
    
    // # Merge options
    const opts = typeof options === 'boolean' 
      ? { ...defaultOptions, forceRefresh: options } 
      : { ...defaultOptions, ...options };
    
    console.log('[SessionManager] Initializing session', {
      uid: user.uid,
      forceRefresh: opts.forceRefresh,
      sessionExists: !!this._session
    });
    
    try {
      // # Check if session already exists and is valid
      if (this._session && !opts.forceRefresh) {
        // # Update last active timestamp
        this._session.lastActive = new Date().toISOString();
        console.log('[SessionManager] Using existing session');
        return this._session;
      }
      
      console.log('[SessionManager] Creating new session');
      
      // # Get user token result to check claims
      const idTokenResult = await user.getIdTokenResult();
      
      // # Get device info
      const deviceInfo = await this._getDeviceInfo();
      
      // # Create session object
      this._session = {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        lastActive: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        deviceInfo,
        claims: idTokenResult.claims,
        deviceLimitReached: false
      };
      
      // # Set role information from claims
      const isAdmin = !!idTokenResult.claims.admin;
      const isTutor = !!idTokenResult.claims.tutor;
      const isContentReviewer = !!idTokenResult.claims.content_reviewer;
      const isFinancialManager = !!idTokenResult.claims.financial_manager;
      const isDesigner = !!idTokenResult.claims.designer;
      const isSupport = !!idTokenResult.claims.support;
      
      // # Determine role based on claims
      let userRole = 'user';
      if (isAdmin) userRole = 'admin';
      else if (isTutor) userRole = 'tutor';
      else if (isContentReviewer) userRole = 'content_reviewer';
      else if (isFinancialManager) userRole = 'financial_manager';
      else if (isDesigner) userRole = 'designer';
      else if (isSupport) userRole = 'support';
      
      // # Update session with role information
      this._session.isAdmin = isAdmin;
      this._session.isTutor = isTutor;
      this._session.isContentReviewer = isContentReviewer;
      this._session.isFinancialManager = isFinancialManager;
      this._session.isDesigner = isDesigner;
      this._session.isSupport = isSupport;
      this._session.userRole = userRole;
      
      // # Load subscription data using subscription manager
      try {
        const subscriptions = await subscriptionManager.loadSubscriptionData(user.uid, opts.forceRefresh);
        this._session.subscriptions = subscriptions;
      } catch (error) {
        console.error('Error loading subscription data:', error);
        // # Continue without subscription data
        this._session.subscriptions = {};
      }
      
      // # Update subscription manager with session and roles
      subscriptionManager.setSession(this._session);
      subscriptionManager.setAdminStatus(isAdmin);
      subscriptionManager.setTutorStatus(isTutor);
      subscriptionManager.setUserRole(userRole);
      
      // # Setup timeouts
      this._setupTimeouts();
      
      // # Initialize Firestore listeners
      this.initializeListeners(user.uid);
      
      // # Broadcast session update
      this._broadcastSessionUpdate();
      
      console.log('[SessionManager] Session initialized successfully');
      return this._session;
    } catch (error) {
      console.error('Error initializing session:', error);
      this._session = null;
      return null;
    }
  }

  // # Get current session
  getSession() {
    return this._session;
  }

  // # Check if session is active
  isSessionActive() {
    if (!this._session) {
      return false;
    }
    
    // # Check if session has expired
    const lastActive = new Date(this._session.lastActive).getTime();
    const now = Date.now();
    
    return (now - lastActive) < this.INACTIVITY_TIMEOUT;
  }

  // # Clear session
  async clearSession() {
    // # Clear timeouts
    this._clearTimeouts();
    
    // # Remove all Firestore listeners
    if (this._session?.uid) {
      this.removeAllListeners();
    }
    
    // # Clear session data
    this._session = null;
    
    // # Clear cache
    cacheManager.clearCache();
    
    // # Clear subscription manager resources
    subscriptionManager.clearAccessCache();
    
    // # Broadcast session clear
    if (this._sessionChannel) {
      this._sessionChannel.postMessage({
        type: 'session_clear'
      });
    }
    
    return true;
  }

  // # Set admin status
  setAdminStatus(isAdmin) {
    if (!this._session) {
      subscriptionManager.setAdminStatus(isAdmin);
      return false;
    }
    
    this._session.isAdmin = isAdmin;
    
    // # Update subscription manager
    subscriptionManager.setAdminStatus(isAdmin);
    
    // # Update role if admin
    if (isAdmin) {
      this._session.userRole = 'admin';
      this.setUserRole('admin');
    }
    
    // # Broadcast session update
    this._broadcastSessionUpdate();
    
    return true;
  }

  // # Set tutor status
  setTutorStatus(isTutor) {
    if (!this._session) {
      subscriptionManager.setTutorStatus(isTutor);
      return false;
    }
    
    this._session.isTutor = isTutor;
    
    // # Update subscription manager
    subscriptionManager.setTutorStatus(isTutor);
    
    // # Update role if tutor
    if (isTutor && this._session.userRole !== 'admin') {
      this._session.userRole = 'tutor';
      this.setUserRole('tutor');
    }
    
    // # Broadcast session update
    this._broadcastSessionUpdate();
    
    return true;
  }

  // # Set user role
  setUserRole(role) {
    if (!this._session) {
      subscriptionManager.setUserRole(role);
      return false;
    }
    
    this._session.userRole = role;
    
    // # Update role flags
    this._session.isAdmin = role === 'admin';
    this._session.isTutor = role === 'tutor';
    this._session.isContentReviewer = role === 'content_reviewer';
    this._session.isFinancialManager = role === 'financial_manager';
    this._session.isDesigner = role === 'designer';
    this._session.isSupport = role === 'support';
    
    // # Update subscription manager
    subscriptionManager.setUserRole(role);
    subscriptionManager.setAdminStatus(this._session.isAdmin);
    subscriptionManager.setTutorStatus(this._session.isTutor);
    
    // # Broadcast session update
    this._broadcastSessionUpdate();
    
    return true;
  }

  // # Get user role
  getUserRole() {
    return this._session ? this._session.userRole : 'user';
  }

  // # Check if user has a specific role
  hasRole(role) {
    if (!this._session) {
      return false;
    }
    
    switch (role) {
      case 'admin':
        return this._session.isAdmin;
      case 'tutor':
        return this._session.isTutor;
      case 'content_reviewer':
        return this._session.isContentReviewer;
      case 'financial_manager':
        return this._session.isFinancialManager;
      case 'designer':
        return this._session.isDesigner;
      case 'support':
        return this._session.isSupport;
      case 'user':
        return true; // Everyone has user role
      default:
        return false;
    }
  }

  // # Refresh session
  async refreshSession() {
    if (!this._session) {
      return false;
    }
    
    try {
      // # Validate session with server
      const result = await this._validateSession();
      
      if (result.data.valid) {
        // # Reset timeouts
        this._setupTimeouts();
        return true;
      } else {
        // # Clear session if invalid
        await this.clearSession();
        return false;
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  }

  // # Get subscription status - delegates to subscription manager
  getSubscriptionStatus() {
    return subscriptionManager.getSubscriptionStatus();
  }

  // # Cleanup on destroy
  destroy() {
    // # Clear timeouts
    this._clearTimeouts();
    
    // # Remove all Firestore listeners
    this.removeAllListeners();
    
    // # Destroy subscription manager
    subscriptionManager.destroy();
    
    // # Close channel
    if (this._sessionChannel) {
      this._sessionChannel.close();
    }
  }

  // # Check if user has a specific subscription - delegates to subscription manager
  isSubscribed(subscriptionType) {
    return subscriptionManager.isSubscribed(subscriptionType);
  }

  // # Check if user has access to content - delegates to subscription manager
  async checkContentAccess(contentType, uid) {
    return subscriptionManager.checkContentAccess(contentType, uid);
  }

  // # Check if user has subscription for content - delegates to subscription manager
  async checkUserSubscription(contentType, uid) {
    return subscriptionManager.checkUserSubscription(contentType, uid);
  }

  // # Get cached access - delegates to subscription manager
  getCachedAccess(contentType, uid) {
    return subscriptionManager.getCachedAccess(contentType, uid);
  }

  // # Clear access cache - delegates to subscription manager
  clearAccessCache() {
    subscriptionManager.clearAccessCache();
  }

  // # Clear user access cache - delegates to subscription manager
  clearUserAccessCache(uid) {
    subscriptionManager.clearUserAccessCache(uid);
  }

  // # Initialize session reference from parent SessionManager
  setSession(session) {
    this._session = session;
    
    // # Update subscription manager with session and roles
    subscriptionManager.setSession(this._session);
    if (session) {
      subscriptionManager.setAdminStatus(session.isAdmin || false);
      subscriptionManager.setTutorStatus(session.isTutor || false);
      subscriptionManager.setUserRole(session.userRole || 'user');
    }
  }
}

// # Create singleton instance
const sessionManager = new SessionManager();

// # Export singleton
export default sessionManager;
